<script setup lang="ts">
// Vue
import { ref, onMounted, computed } from "vue";

// Dependencies
import { useRouter } from "vue-router";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import { v4 as uuidv4 } from "uuid";

// Types
import { Report } from "@/types/Views/ProjectDashboard/Report";

// Services
import { getDownloadReport, getGeneratedReports } from "@/api/services/Emission";
import {
  generateSwedishExcelReport,
  getGenerateDgnbDocumentationReport,
  getGenerateDocumentationReport,
  getGenerateLca2025DocumentationReport,
  getGenerateLcaDocumentationReport,
  getGenerateXmlDocumentationReport,
} from "@/api/services/Mapping";

// Features
import { useLocalization, Country_Enum } from "@/providers/Localization";

// Components
import Button from "@/components/Reusables/Buttons/Button.vue";
import TagItem from "@/components/Reusables/Utils/TagItem.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import ReportOption from "./components/ReportOption.vue";
import { openFlyoutId } from "@/features/PageNavItem";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import classificationTypeId from "@/services/enums/classificationTypeId";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import { useClassificationTypes } from "@/dataProviders/ClassificationTypes";
import { resolveClassificationDefaults } from "@/features/BuildingProject";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";
import calculationTypeId from "@/services/enums/calculationTypeId";
import CalculationTypes from "@/dataProviders/CalculationTypes/CalculationTypes.vue";
import ClassificationTypes from "@/dataProviders/ClassificationTypes/ClassificationTypes.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import { downloadBlob } from "@/services/Utils/downloadBlob";
import useProjectEditReadOnly from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";

/** `method` keeps `Promise<any>`: the generators return several different response shapes, and
 *  narrowing it needs a type guard at the call site — a separate change. */
type ReportDownload = {
  id: string;
  name: string;
  description: string;
  type: string;
  format: string;
  method: (id: string) => Promise<any> | void;
  predicate?: () => boolean;
  showDisclaimer: boolean;
};

/** The subset the generate-and-download flow actually needs. */
type ReportGeneration = Pick<ReportDownload, "id" | "type" | "method">;

const props = defineProps<{
  buildingProjectId: string;
}>();

// Raised when a row navigates instead of downloading: the sidebar flyout closes itself via
// openFlyoutId, but MobileMenu owns its own open state and listens for this to dismiss the drawer.
const emit = defineEmits<{
  navigate: [];
}>();

const router = useRouter();
const localization = useLocalization();
const project = useProjectWrapper();
const calculationTypes = useCalculationTypes();
const classificationTypes = useClassificationTypes();
const modals = useModalStore();
const toast = useToast();

// Generating is a write, so it obeys the same read-only guard as other write surfaces: a Viewer can't
// write, and a preview isn't where an export should land — exports always name the CURRENT revision.
const { blockedByPreview } = useProjectEditReadOnly();

/** Not the stored value alone: an untouched project has no speckle_parameter_config row, so this must
 *  match the API's fallback (HasuraService.GetSpeckleParameterConfig) for DGNB availability to agree. */
const isBimTypeCode = computed(
  () =>
    resolveClassificationDefaults(
      classificationTypes.getClassificationTypes(),
      project.getProjectWrapper()?.project?.speckleConfiguration
    ).classificationTypeId === classificationTypeId.BimTypeCode
);

const calculationType = computed(() => {
  const calculationTypeId =
    project.getProjectWrapper().project.emissionCalculationAssumptions.calculationType.calculationTypeEnum;

  return calculationTypeId !== null ? calculationTypes.getSelectedCalculationType(calculationTypeId) : undefined;
});

const isBR2025VariantCalculationType = computed(() => {
  return calculationType.value?.variant?.variant === calculationTypeVariant.BR182025 || false;
});

const isCustomCalculationType = computed(() => {
  return calculationType.value?.id === calculationTypeId.CUSTOM_DK || false;
});

const generatedReports = ref<Report[] | null>(null);
const isGeneratingReport = ref<string>("");
// Separate from `generatedReports === null` so the list can tell "still fetching" from "none yet".
const hasLoadedReports = ref<boolean>(false);

// A country without its own report list gets the generic one.
const getDownloadsByCountry = (country: Country_Enum | "DEFAULT") => {
  const result = { ...(downloads[country] ?? downloads.DEFAULT) };

  for (const key in result) {
    const download = result[key];

    if (download.predicate !== undefined && !download.predicate()) {
      delete result[key];
    }
  }

  return result;
};

async function updateGeneratedReports() {
  // Assigns an array either way, so "none yet" is distinguishable from "not loaded".
  try {
    const response = await getGeneratedReports(props.buildingProjectId);

    // Sort response by item.createdAt
    generatedReports.value = response.sort((a: any, b: any) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  } catch (error) {
    generatedReports.value = [];
    toast.error("Could not load previously generated reports");
    Sentry.captureException(error);
  } finally {
    hasLoadedReports.value = true;
  }
}

async function generateAndDownloadReport({ type, method, id }: ReportGeneration) {
  // RevisionGatedButton styles and explains the block but deliberately does not swallow the click,
  // so the refusal is spoken here - as on every other gated handler.
  if (blockedByPreview()) return;

  // If already generating a report, return
  if (isGeneratingReport.value.length > 0) return;

  // Set generating report to id
  isGeneratingReport.value = id;

  // Initiate generation of the report & catch the error if it fails
  try {
    const result = await method(props.buildingProjectId);
    if (result === false || result?.error) {
      throw new Error("Report generation failed");
    }
  } catch (error) {
    toast.error("Report generation failed");
    // Was swallowed: the toast told the user, but nothing told us, so a backend generation failure
    // left no trace to debug from.
    Sentry.captureException(error);
    isGeneratingReport.value = "";
    return;
  }

  // Everything from here on clears the generating flag on the way out — without the finally, a failed
  // download left the button stuck reading "Generating" and blocked a retry.
  try {
    // Get all generated reports
    await updateGeneratedReports();

    // Thrown rather than returned: the report generated fine but the listing hasn't caught up, and a
    // bare return would silently reset the button as if nothing had happened.
    const createdReport = generatedReports.value?.[0];
    if (!createdReport) throw new Error("Report generated but not yet listed by reportblobs");

    // Download the created report
    const downloadResponse = await getDownloadReport(createdReport.id, props.buildingProjectId);

    // Create a blob of the downloaded report
    const blob = new Blob([downloadResponse], {
      type,
    });

    downloadBlob(blob, createdReport.blobPath);

    // Show a success toast
    toast.success("Report downloaded");
  } catch (error) {
    toast.error("Could not download the report");
    Sentry.captureException(error);
  } finally {
    isGeneratingReport.value = "";
  }
}

// Country variants that carry a disclaimer gate generation behind it: generation starts only once
// the modal resolves with a confirmation, so dismissing it leaves the project untouched.
async function confirmDisclaimerThenGenerate(download: ReportGeneration) {
  // Refused before the disclaimer opens, for the same reason the in-flight check below is: reading a
  // whole disclaimer and pressing Understood should never end in nothing happening.
  if (blockedByPreview()) return;

  // Checked before opening the disclaimer, not after: generateAndDownloadReport refuses mid-flight,
  // and checking only after would let the user click Understood and get nothing.
  if (isGeneratingReport.value.length > 0) return;

  const result = await modals.open(
    "ReportDisclaimerModal",
    {},
    { wrapperClass: "max-w-[560px]! min-h-0!", ariaLabel: "Disclaimer" }
  );
  if (result?.action !== "confirm") return;

  await generateAndDownloadReport(download);
}

// Navigates to the in-app LCA report page instead of generating a file; clears the flyout/drawer
// state before pushing so neither lingers behind the report page.
const goToLcaReport = async () => {
  openFlyoutId.value = null;
  emit("navigate");

  try {
    await router.push({ name: "projectLcaPdfReportPage", params: { id: props.buildingProjectId } });
  } catch (error) {
    // Re-clicking the row while already on the report is a duplicated navigation, not a failure.
    if ((error as { name?: string })?.name === "NavigationDuplicated") return;
    toast.error("Could not open the LCA report");
    Sentry.captureException(error);
  }
};

const downloads: Record<string, Record<string, ReportDownload>> = {
  DEFAULT: {
    genericDocumentation: {
      id: uuidv4(),
      name: "Generic LCA documentation report",
      description: "LCA documentation for countries without a national report format",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      method: getGenerateDocumentationReport,
      showDisclaimer: false,
    },
  },
  [Country_Enum.DENMARK]: {
    lcaDocumentation: {
      id: uuidv4(),
      name: "LCA Documentation (Danish)",
      description: "Standardformat til LCA-dokumentation ifbm. færdigmelding",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      predicate: () => !isBR2025VariantCalculationType.value && !isCustomCalculationType.value,
      method: getGenerateLcaDocumentationReport,
      showDisclaimer: false,
    },
    lca2025Documentation: {
      id: uuidv4(),
      name: "LCA Documentation (Danish)",
      description: "Standardformat til LCA-dokumentation ifbm. færdigmelding",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      predicate: () => isBR2025VariantCalculationType.value || isCustomCalculationType.value,
      method: getGenerateLca2025DocumentationReport,
      showDisclaimer: false,
    },
    dgnbDocumentation: {
      id: uuidv4(),
      name: "DGNB Documentation (Danish)",
      description: "DGNB-dokumentation til Conformity Check",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      method: getGenerateDgnbDocumentationReport,
      predicate: () => isBimTypeCode.value,
      showDisclaimer: false,
    },
  },
  [Country_Enum.FAROE_ISLAND]: {
    lcaDocumentation: {
      id: uuidv4(),
      name: "LCA Documentation (Danish)",
      description: "Standardformat til LCA-dokumentation ifbm. færdigmelding",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      method: getGenerateLcaDocumentationReport,
      showDisclaimer: false,
    },
  },
  [Country_Enum.NORWAY]: {
    lcaDocumentation: {
      id: uuidv4(),
      name: "LCA Documentation (Norwegian)",
      description: "Standardformat for LCA-dokumentasjon",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      method: getGenerateLcaDocumentationReport,
      showDisclaimer: false,
    },
  },
  [Country_Enum.SWEDEN]: {
    lcaDocumentation: {
      id: uuidv4(),
      name: "LCA Documentation (Swedish)",
      description: "Standardformat för LCA-dokumentation",
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      format: ".xlsx",
      method: generateSwedishExcelReport,
      showDisclaimer: false,
    },
    xmlDocumentation: {
      id: uuidv4(),
      name: "Boverket XML (Swedish)",
      description: "Standardformat för Klimatdeklaration",
      type: "application/xml",
      format: ".xml",
      method: getGenerateXmlDocumentationReport,
      showDisclaimer: false,
    },
  },
};

onMounted(async () => {
  await updateGeneratedReports();
});

const country = computed(() => localization.getCountry() ?? "DEFAULT");
</script>

<template>
  <CalculationTypes :country="localization.country" />
  <!-- Loads classification types here too: this flyout's report list depends on them, and the store
       is otherwise only filled by settings/material-mapping — must not depend on prior navigation. -->
  <ClassificationTypes :country="localization.country" />

  <div class="flex min-w-[200px] flex-col gap-2">
    <div
      v-for="download in getDownloadsByCountry(country)"
      :key="download.id"
      class="rounded-8 bg-slate-0 flex gap-4 border border-slate-200 p-3">
      <!-- Row contents text -->
      <div class="flex min-w-0 flex-col">
        <!-- Wraps and truncates: the column is min-w-0 so it can shrink, and a nowrap heading with
             nothing to clip it spilled under the Generate button at mobile widths instead. -->
        <h4 class="t-small-bold text-type-default mb-1 flex flex-wrap items-baseline gap-2">
          <span class="min-w-0 truncate">{{ download.name }}</span>
          <TagItem
            v-if="download.format"
            class="self-baseline"
            color="neutral"
            :label="download.format" />
        </h4>
        <p class="t-xs text-type-muted">{{ download.description }}</p>
      </div>

      <!-- Colour stays primary while this row generates: `disabled` leaves the spinner with a fill
           Loading Spinner has no branch for, so the arc vanishes and the button reads as a blank capsule. -->
      <RevisionGatedButton
        v-if="download.name !== 'Coming Soon'"
        :guard="useProjectEditReadOnly"
        :is-loading="isGeneratingReport === download.id"
        :disabled="isGeneratingReport.length > 0 && isGeneratingReport !== download.id"
        :color="isGeneratingReport.length > 0 && isGeneratingReport !== download.id ? 'disabled' : 'primary'"
        label="Generate"
        size="sm"
        class="ml-auto shrink-0 self-center"
        data-cy="generate-report-btn"
        @click="
          () => {
            download.showDisclaimer
              ? confirmDisclaimerThenGenerate({
                  type: download.type,
                  method: download.method,
                  id: download.id,
                })
              : generateAndDownloadReport({
                  type: download.type,
                  method: download.method,
                  id: download.id,
                });
          }
        " />
    </div>

    <Divider spacing="sm" />

    <div class="rounded-8 bg-slate-0 flex gap-4 border border-slate-200 p-3">
      <div class="flex min-w-0 flex-col">
        <h4 class="t-small-bold text-type-default mb-1 flex flex-wrap items-baseline gap-2">
          <span class="min-w-0 truncate">LCA Report</span>
          <TagItem
            class="self-baseline"
            color="neutral"
            label=".pdf" />
        </h4>
        <p class="t-xs text-type-muted">Real-Time LCA report for this project</p>
      </div>

      <Button
        label="Open"
        color="primary"
        size="sm"
        class="ml-auto shrink-0 self-center"
        data-cy="open-lca-report-btn"
        @click="goToLcaReport" />
    </div>
  </div>

  <!-- px-3 lines this section up with the cards' inner inset above it, so the label, card titles
       and filenames start on the same edge. -->
  <template v-if="hasLoadedReports">
    <h3 class="t-small-bold text-type-default mt-6 mb-1 px-3">Previously generated reports</h3>
    <p
      v-if="!generatedReports?.length"
      class="t-xs text-type-muted px-3">
      No reports generated yet.
    </p>
    <!-- No key on this wrapper: it used to key on the list length, which re-mounted every row when a
         generation finished and threw away the focus of anyone tabbing the list. -->
    <div
      v-else
      class="flex flex-col">
      <ReportOption
        v-for="report in generatedReports"
        :key="report.id"
        :report="report"
        :building-project-id="buildingProjectId" />
    </div>
  </template>
</template>
